import React from 'react'
import store from '../store'
import { setEcho } from './index'
import setLoggedUser from './set-logged-user'
import {
    AUTH_UPDATE_FORM_VALUE,
    SET_FORM_FAILURE,
    SET_FORM_SUCCESS,
    CLEAR_FORM_SUCCESS_AND_ERROR,
    CLEAR_FORM_DATA,
    SET_EMAIL_CHECK_RESPONSE_ID,
    GOTO_REGISTRATION_CURRENT_STEP,
    LOAD_LAST_LOGGED_USER_DATA,
    SET_REDIRECT_URL,
    CLEAR_REDIRECT_URL,
} from '../reducers/auth-forms'
import i18n from '../../locales/i18n'
import {
    LOGIN_ERROR_TOO_MUCH_INVALID_CREDENTIALS,
    PASSWORD_MIN_LENGTH,
} from '../../consts'

/**
 * TODO remove when old auth forms will be removed
 * Set authorization
 *
 * @param {string} email
 * @param {string} password
 * @param {null|function|Object|string} redirect
 * @return {Promise}
 */
export default (email, password, redirect = null) => {
    store.dispatch({
        type: 'TOGGLE_BUSY_SECTION',
        section: 'login',
    })
    let json = {
        grant_type: 'password',
        client_id: window.APP_CONFIG.REACT_APP_BACKEND_AUTH_ID,
        client_secret: window.APP_CONFIG.REACT_APP_BACKEND_AUTH_SECRET,
        username: email,
        password,
        scope: '*',
    }

    const defaultHeaders = {
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
        },
    }

    // use axios instance without interceptors
    return window.axios.post(`${window.APP_CONFIG.REACT_APP_API_URL}api/oauth/token`, json, defaultHeaders)
        .then(response => {
            if (!response || !response?.data) {
                return Error
            }

            store.dispatch({
                type: 'SET_STORE_TOKENS',
                tokens: response?.data,
            })
            setEcho(response?.data)

            return setLoggedUser(response?.data, false, true).then(() => {
                const successAction = store.getState().app.login.successAction

                if (typeof successAction === 'function') {
                    successAction()
                } else {
                    if (redirect !== false) {
                        if (typeof redirect === 'function') {
                            redirect(response?.data)
                        } else {
                            if (redirect) {
                                window.location.href = redirect
                            } else {
                                window.location.reload()
                            }
                        }
                    }
                }
            })
        }).catch(err => {
            if (!err) {
                store.dispatch({
                    type: 'SET_LOGIN_ERROR',
                    error: 'too_much_invalid_credentials',
                })
            }

            if (err && err.response) {
                store.dispatch({
                    type: 'SET_LOGIN_ERROR',
                    error: err.response.data.error,
                })
            }

            if (err && err.data && err.data.error) {
                store.dispatch({
                    type: 'SET_LOGIN_ERROR',
                    error: err.data.error,
                })
            }

            store.dispatch({
                type: 'TOGGLE_BUSY_SECTION',
                section: 'login',
            })

            return Promise.reject(err)
        })
}

/**
 * Possible form types
 * @typedef {'loginForm' | 'signupForm' | 'forgottenPasswordForm' | 'lastLoggedUserForm'} FormTypes
 */

/**
 * Clear redirect URL after success login
 * @returns {Promise}
 */
export const clearRedirectUrl = () => (
    store.dispatch({
        type: CLEAR_REDIRECT_URL,
    })
)

/**
 * Set redirect URL after success login
 * @param {string} redirectUrl
 */
export const setRedirectUrl = redirectUrl => {
    store.dispatch({
        type: SET_REDIRECT_URL,
        redirectUrl,
    })
}

/**
 * Action for modifying form input value
 * @param {FormTypes} formType - Type of the form
 * @param {string} formField - Name of the input
 * @param {*} value - Value to save
 */
export const updateFormValueAction = (formType, formField, value) => {
    store.dispatch({
        type: AUTH_UPDATE_FORM_VALUE,
        formType,
        formField,
        value,
    })
}

/**
 * Action for form request failure
 * @param {FormTypes} formType - Type of the form
 * @param {Object} errors - Response errors
 */
export const setFormFailureAction = (formType, errors) => {
    store.dispatch({
        type: SET_FORM_FAILURE,
        formType,
        errors,
    })
}

/**
 * Action for form request success
 * @param {FormTypes} formType - Type of the form
 * @param {Object} responseData - Response data
 * @returns {Promise}
 */
export const setFormSuccessAction = (formType, responseData) => (
    store.dispatch({
        type: SET_FORM_SUCCESS,
        formType,
        responseData,
    })
)

/**
 * Action for clearing auth form success and error
 * @param {FormTypes} formType - Type of the form
 */
export const clearFormSuccessAndErrorAction = formType => {
    store.dispatch({
        type: CLEAR_FORM_SUCCESS_AND_ERROR,
        formType,
    })
}

/**
 * Action for clearing auth forms redux data
 */
export const clearFormDataAction = () => {
    store.dispatch({
        type: CLEAR_FORM_DATA,
    })
}

/**
 * Save response id after check email exists into state
 * @param {FormTypes} formType
 * @param {number|null} responseId
 */
export const setCheckEmailResponseIdAction = (formType, responseId) => {
    store.dispatch({
        type: SET_EMAIL_CHECK_RESPONSE_ID,
        formType,
        responseId,
    })
}

/**
 * Change current registration step in responsive form
 * @param {number} step
 */
export const gotoRegistrationStepAction = step => {
    store.dispatch({
        type: GOTO_REGISTRATION_CURRENT_STEP,
        formType: 'signupForm',
        step,
    })
}

/**
 * Load last logged user data into store
 */
export const loadLastLoggedUserDataAction = () => {
    store.dispatch({
        type: LOAD_LAST_LOGGED_USER_DATA,
    })
}

/**
 * Throws cognito login validation errors
 * @param {string|Object} error
 * @returns {null}
 */
export const throwLoginValidationError = error => {
    // too many attempts failed login
    if (error === LOGIN_ERROR_TOO_MUCH_INVALID_CREDENTIALS) {
        return window.swalert(i18n.t('tooManyInvalidCredentials'))
    }

    // sportnect errors
    if (error === 'wrong_password') {
        return window.swalert(i18n.t('inputtedWrongPassword'))
    }

    // our custom error for facebook password logins
    if (error?.code === 'UserFacebookPasswordLoginException') {
        return window.swalert('Účet byl registrován přes Facebook nebo Apple. Prosím, přihlaste se přes danou službu. Pokud máte jiný problém nebo potřebujete k tomuto emailu zaregistrovat heslo. Kontaktujte nás na sportnect@sportnect.com')
    }

    // cognito errors
    if (error?.code === 'UserNotFoundException') {
        return window.swalert(i18n.t('signInUserNotFound'))
    }

    if (error?.code === 'NotAuthorizedException') {
        return window.swalert(i18n.t('inputtedWrongPassword'))
    }

    if (error?.code === 'PasswordResetRequiredException') {
        return window.swalert(i18n.t('passwordResetRequiredException'))
    }

    return window.swalert(i18n.t('serverErrorMessage'))
}

/**
 * Throws cognito validatino errors
 * @param {Object} error
 * @returns {*}
 */
export const throwRegistrationValidationError = error => {
    if (error?.code === 'InvalidParameterException') {
        return window.swalert(i18n.t('passwordIsShortMessage', { minLength: PASSWORD_MIN_LENGTH }))
    }

    if (error?.code === 'InvalidPasswordException') {
        return window.swalert(i18n.t('invalidPasswordException'))
    }

    if (error?.code === 'TooManyRequestsException') {
        return window.swalert(i18n.t('tooManyRequestsException'))
    }

    if (error?.code === 'UsernameExistsException') {
        return window.swalert(i18n.t('usernameExistsException', {
            loginLink: (
                <a
                    href={`${window.APP_CONFIG.REACT_APP_FRONTEND_URL}/${window.APP_CONFIG.REACT_APP_AUTH_ROUTES.login}`}
                    className="text-lowercase"
                >
                    {i18n.t('login')}
                </a>
            ),
        }))
    }

    return window.swalert(i18n.t('serverErrorMessage'))
}

/**
 * Throws cognito password reset validatino errors
 * @param {Object} error
 * @returns {*}
 */
export const throwPasswordResetValidationError = error => {
    if (error?.code === 'CodeMismatchException') {
        return window.swalert(i18n.t('newPasswordTokenMismatchError'))
    }

    if (error?.code === 'ExpiredCodeException') {
        return window.swalert(i18n.t('newPasswordTokenExpiredError'))
    }

    if (error?.code === 'LimitExceededException') {
        return window.swalert(i18n.t('newPasswordAttemptLimitExceededError'))
    }

    return window.swalert(i18n.t('serverErrorMessage'))
}

/**
 * Request forgotten password to API
 * @param {string} email
 * @return {AxiosPromise<any>}
 */
export const requestForgottenPasswordToAPI = email =>
    window.axios.post(`${window.APP_CONFIG.REACT_APP_API_URL}api/password/email`, { email: email })

/**
 * Check, if email in signup form exists or not and save response id in signup form in state
 * @param {string} email
 * @return {Promise}
 */
export const checkIfEmailExists = email => {
    const formType = 'signupForm'

    const formData = {
        email,
    }

    return window.axios.post(`${window.APP_CONFIG.REACT_APP_API_URL}api/check/email`, formData)
        .then(({ data }) => {
            setCheckEmailResponseIdAction(formType, data.id)
        })
        .catch(errorResponse => {
            setFormFailureAction(formType, errorResponse.data)
        })
}
